import { useState } from "react";
import { Bot, Coins } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { cn, getModelThumbnailUrl } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import type { LLMModel } from "@/api/models/api-types";

interface LLMModelTooltipContentProps {
  model: LLMModel;
}

export const LLMModelTooltipContent = ({
  model,
}: LLMModelTooltipContentProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { i18n, t } = useTranslation();

  // Multi-language description desteği
  const getCurrentDescription = () => {
    if (!model.description) return "";
    const currentLang = i18n.resolvedLanguage || "en";
    const found = model.description[
      currentLang as keyof typeof model.description
    ] as string;
    return found || model.description.en || "";
  };

  const description = getCurrentDescription();

  // Get thumbnail URL (R2 veya thumbnail_url)
  const thumbnailUrl = getModelThumbnailUrl(model);

  return (
    <Tooltip.Content
      className="max-w-[400px] bg-black/95 backdrop-blur-sm rounded-2xl p-4 shadow-2xl border border-white/20 z-[2000] pointer-events-auto glass-enhanced"
      side="top"
      sideOffset={15}
      align="center"
      collisionPadding={15}
      onPointerDownOutside={(e) => {
        // Tooltip içi elementler için tooltip kapanmasın
        const target = e.target as HTMLElement;
        if (target.closest("[data-radix-tooltip-content]")) {
          e.preventDefault();
        }
      }}
    >
      <div className="relative">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          {/* Model Image */}
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
            {thumbnailUrl && !imageError ? (
              <img
                src={thumbnailUrl}
                alt={model.title}
                className={cn(
                  "w-full h-full object-cover transition-opacity duration-300",
                  imageLoaded ? "opacity-100" : "opacity-0"
                )}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800">
                <Bot className="w-6 h-6 mb-1" />
                <span className="text-xs">{t("llmSelection.llmModel")}</span>
              </div>
            )}
            {thumbnailUrl && !imageLoaded && !imageError && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          {/* Model Info */}
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-white text-sm mb-1 line-clamp-2">
              {model.title}
            </h4>
            <div className="text-xs text-gray-300 mb-2">
              {model.provider?.name || t("llmSelection.unknown")}
            </div>

            {/* Pricing Info */}
            <div className="flex items-center gap-2 text-xs text-gray-300">
              <Coins className="w-3 h-3" />
              <span>
                {model.price
                  ? `${model.price.toFixed(3)} ${t("llmSelection.credits")}`
                  : `${t("llmSelection.input")} ${(
                      model.pricing?.inputTokenPrice ?? 0
                    ).toFixed(3)} / ${t("llmSelection.output")} ${(
                      model.pricing?.outputTokenPrice ?? 0
                    ).toFixed(3)}`}
              </span>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="text-sm text-gray-200 leading-relaxed">
          {description}
        </div>

        {/* Model Details */}
        <div className="mt-3 pt-3 border-t border-white/10">
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <span className="text-gray-400">{t("llmSelection.modelId")}</span>
              <div className="text-gray-200 font-mono">{model.name}</div>
            </div>
            <div>
              <span className="text-gray-400">
                {t("llmSelection.provider")}
              </span>
              <div className="text-gray-200">
                {model.provider?.name || t("llmSelection.unknown")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Tooltip.Content>
  );
};
